import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarIcon, MapPin, Search } from "lucide-react";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";

export default function SearchBox({ className = "", initialValues = {} }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? enUS : es;
  const [location, setLocation] = useState(initialValues.location || "Tijuana");
  const [pickupDate, setPickupDate] = useState(initialValues.pickupDate || null);
  const [returnDate, setReturnDate] = useState(initialValues.returnDate || null);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set("city", location);
    if (pickupDate) params.set("pickup", pickupDate.toISOString());
    if (returnDate) params.set("return", returnDate.toISOString());
    navigate(`/search?${params.toString()}`);
  };

  return (
    <form
      onSubmit={handleSearch}
      className={`bg-white rounded-2xl border border-neutral-200 shadow-subtle p-2 ${className}`}
      data-testid="search-box"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        {/* Location */}
        <div className="md:col-span-4 relative">
          <div className="flex items-center h-14 px-4 rounded-xl hover:bg-neutral-50 transition-colors cursor-pointer">
            <div className="flex-1">
              <p className="text-xs font-medium text-neutral-500">{t("searchBox.location")}</p>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-neutral-400" />
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t("searchBox.whereTo")}
                  className="border-0 p-0 h-auto text-[15px] font-medium focus-visible:ring-0 bg-transparent placeholder:text-neutral-400"
                  data-testid="search-location"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:block w-px bg-neutral-200 my-3" />

        {/* Pickup Date */}
        <div className="md:col-span-3">
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex items-center h-14 px-4 rounded-xl hover:bg-neutral-50 transition-colors cursor-pointer">
                <div className="flex-1">
                  <p className="text-xs font-medium text-neutral-500">{t("searchBox.pickup")}</p>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-neutral-400" />
                    <span className={`text-[15px] font-medium ${pickupDate ? "text-[#111111]" : "text-neutral-400"}`}>
                      {pickupDate ? format(pickupDate, "dd MMM", { locale: dateLocale }) : t("searchBox.addDate")}
                    </span>
                  </div>
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={pickupDate}
                onSelect={setPickupDate}
                disabled={(date) => date < new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="hidden md:block w-px bg-neutral-200 my-3" />

        {/* Return Date */}
        <div className="md:col-span-3">
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex items-center h-14 px-4 rounded-xl hover:bg-neutral-50 transition-colors cursor-pointer">
                <div className="flex-1">
                  <p className="text-xs font-medium text-neutral-500">{t("searchBox.return")}</p>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-neutral-400" />
                    <span className={`text-[15px] font-medium ${returnDate ? "text-[#111111]" : "text-neutral-400"}`}>
                      {returnDate ? format(returnDate, "dd MMM", { locale: dateLocale }) : t("searchBox.addDate")}
                    </span>
                  </div>
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={returnDate}
                onSelect={setReturnDate}
                disabled={(date) => date <= (pickupDate || new Date())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Search Button */}
        <div className="md:col-span-2 flex items-center justify-end px-2">
          <Button
            type="submit"
            className="btn-primary w-full md:w-auto h-12 px-6"
            data-testid="search-submit-btn"
          >
            <Search className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">{t("searchBox.search")}</span>
          </Button>
        </div>
      </div>
    </form>
  );
}
